import { useState, useMemo } from "react";
import { useLang, ui, tx } from "../i18n";
import { Icon } from "./Icon";
import { MUNICIPALITIES, REGIONS } from "../data/municipalities";
import { AGENCIES } from "../data/agencies";
import { HOSPITALS } from "../data/hospitals";
import { MUNI_POLICE, HAS_MUNI_POLICE, SJ_PRECINCTS } from "../data/police";
import { FIRE_STATIONS } from "../data/firestations";

function hav(a, b, c, d) {
  const R = 6371, rad = (x) => (x * Math.PI) / 180;
  const h = Math.sin(rad(c - a) / 2) ** 2 + Math.cos(rad(a)) * Math.cos(rad(c)) * Math.sin(rad(d - b) / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

const NEAREST_FIRE = Object.fromEntries(
  MUNICIPALITIES.map((m) => {
    let best = null, bd = Infinity;
    for (const s of FIRE_STATIONS) {
      const d = hav(m.lat, m.lng, s.lat, s.lng);
      if (d < bd) { bd = d; best = s; }
    }
    return [m.name, { ...best, d: bd }];
  })
);
import { ShelterMap } from "./ShelterMap";
import { ShelterList } from "./ShelterList";

const COLOR_STYLES = {
  red: "bg-red-600 hover:bg-red-700",
  navy: "bg-[#1C2B4A] hover:bg-slate-700",
  amber: "bg-amber-600 hover:bg-amber-700",
  green: "bg-green-700 hover:bg-green-800",
};

function CallButton({ testId, tel, label, sub, color = "navy" }) {
  return (
    <a
      data-testid={testId}
      href={`tel:${tel}`}
      className={`inline-flex items-center gap-2 rounded-md px-4 py-2.5 text-sm font-bold text-white transition-colors duration-200 active:scale-[0.97] ${COLOR_STYLES[color]}`}
    >
      <Icon name="Phone" className="w-4 h-4" />
      {label}
      {sub && <span className="font-mono-pr text-xs opacity-90">{sub}</span>}
    </a>
  );
}

export function DirectorioTab({ lowBandwidth }) {
  const { lang } = useLang();
  const [query, setQuery] = useState("");
  const [region, setRegion] = useState("all");
  const [section, setSection] = useState("municipios");
  const [copied, setCopied] = useState(null);

  const shareText = (m) =>
    lang === "es"
      ? `${m.name}, PR — Emergencias: 911 · OMME: ${m.omme} · NMEAD: 787-724-0124 · Bomberos: 787-343-2330 · Policía PR: 787-343-2020. Más: preparatepr.org`
      : `${m.name}, PR — Emergencies: 911 · OMME: ${m.omme} · NMEAD: 787-724-0124 · Fire: 787-343-2330 · PR Police: 787-343-2020. More: preparatepr.org`;

  const shareMuni = async (m) => {
    const text = shareText(m);
    if (navigator.share) {
      try { await navigator.share({ title: `preparatepr.org — ${m.name}`, text }); } catch {}
    } else {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(m.name);
        setTimeout(() => setCopied(null), 2000);
      } catch {}
    }
  };

  const q = query.trim().toLowerCase();

  const municipalities = useMemo(
    () => MUNICIPALITIES.filter(
      (m) => (region === "all" || m.region === region) && (!q || m.name.toLowerCase().includes(q))
    ),
    [q, region]
  );

  const agencies = useMemo(
    () => AGENCIES.filter((a) => !q || tx(a.name, lang).toLowerCase().includes(q) || tx(a.desc, lang).toLowerCase().includes(q)),
    [q, lang]
  );

  const hospitals = useMemo(
    () => HOSPITALS.filter((h) => (region === "all" || h.region === region) && (!q || h.name.toLowerCase().includes(q) || h.town.toLowerCase().includes(q))),
    [q, region]
  );

  const sections = [
    { id: "municipios", label: tx(ui.directory.municipalities, lang), testId: "dir-section-municipios" },
    { id: "agencias", label: tx(ui.directory.agencies, lang), testId: "dir-section-agencias" },
    { id: "hospitales", label: tx(ui.directory.hospitals, lang), testId: "dir-section-hospitales" },
    { id: "mapa", label: tx(ui.directory.map, lang), testId: "dir-section-mapa" },
    { id: "refugios", label: tx(ui.directory.refugios, lang), testId: "dir-section-refugios" },
  ];

  return (
    <div data-testid="directorio-tab" className="px-4 sm:px-8 lg:px-12 py-8 sm:py-10">
      <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">{tx(ui.directory.title, lang)}</h2>
      <p className="mt-1 mb-6 text-sm sm:text-base text-slate-600">{tx(ui.directory.subtitle, lang)}</p>

      <div className="relative mb-4">
        <Icon name="Search" className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <input
          data-testid="directory-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={tx(ui.directory.search, lang)}
          className="w-full rounded-lg border-2 border-slate-300 bg-white py-3.5 pl-12 pr-4 text-base text-slate-900 placeholder:text-slate-400 focus:border-amber-600 focus:outline-none"
        />
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <button
          data-testid="region-all"
          onClick={() => setRegion("all")}
          className={`rounded-full px-3.5 py-1.5 text-xs font-bold border transition-colors duration-200 ${region === "all" ? "bg-[#1C2B4A] text-white border-[#1C2B4A]" : "border-slate-300 text-slate-600 hover:border-slate-500"}`}
        >
          {tx(ui.directory.allRegions, lang)}
        </button>
        {Object.entries(REGIONS).map(([id, label]) => (
          <button
            key={id}
            data-testid={`region-${id}`}
            onClick={() => setRegion(id)}
            className={`rounded-full px-3.5 py-1.5 text-xs font-bold border transition-colors duration-200 ${region === id ? "bg-[#1C2B4A] text-white border-[#1C2B4A]" : "border-slate-300 text-slate-600 hover:border-slate-500"}`}
          >
            {tx(label, lang)}
          </button>
        ))}
      </div>

      <div className="flex gap-1 mb-6 border-b border-slate-300 overflow-x-auto no-scrollbar">
        {sections.map((s) => (
          <button
            key={s.id}
            data-testid={s.testId}
            onClick={() => setSection(s.id)}
            className={`whitespace-nowrap px-4 py-2.5 text-sm font-bold border-b-[3px] transition-colors duration-200 ${section === s.id ? "border-amber-600 text-[#1C2B4A]" : "border-transparent text-slate-500 hover:text-[#1C2B4A]"}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {section === "municipios" && (
        <>
          <div data-testid="omme-verify-note" className="mb-6 rounded-lg border-l-4 border-amber-600 border border-slate-300 bg-amber-50 p-4 sm:p-5">
            <p className="flex gap-2.5 text-sm text-amber-900 leading-relaxed">
              <Icon name="Info" className="w-5 h-5 shrink-0 text-amber-700" />
              {tx(ui.directory.verifyNote, lang)}
            </p>
            <p data-testid="police-fire-note" className="mt-2.5 flex gap-2.5 text-sm text-amber-900 leading-relaxed">
              <Icon name="Shield" className="w-5 h-5 shrink-0 text-amber-700" />
              {tx(ui.directory.policeFireNote, lang)}
            </p>
          </div>
          <p data-testid="municipality-count" className="mb-4 font-mono-pr text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
            {municipalities.length} {tx(ui.directory.results, lang)}
          </p>
          {municipalities.length === 0 && <p className="text-slate-500">{tx(ui.directory.noResults, lang)}</p>}
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {municipalities.map((m) => (
              <div
                key={m.name}
                data-testid={`muni-${m.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="rounded-lg border border-slate-300 bg-white p-5 transition-colors duration-200 hover:border-amber-500"
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h3 className="font-display text-lg font-bold text-slate-900">{m.name}</h3>
                  <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-bold text-slate-600 shrink-0">
                    {tx(REGIONS[m.region], lang)}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mb-3.5">{tx(ui.directory.omme, lang)}</p>
                {HAS_MUNI_POLICE.has(m.name) && (
                  <p data-testid={`badge-muni-police-${m.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="mb-3.5 inline-flex items-center gap-1.5 rounded-full bg-green-100 px-2.5 py-1 text-[11px] font-bold text-green-800">
                    <Icon name="Shield" className="w-3.5 h-3.5" />
                    {lang === "es" ? "Policía municipal propia" : "Own municipal police"}
                  </p>
                )}
                <p data-testid={`nearest-fire-${m.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="mb-3.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-slate-500">
                  <Icon name="Flame" className="w-3.5 h-3.5 text-red-600 shrink-0" />
                  <span>{lang === "es" ? "Estación de bomberos más cercana" : "Nearest fire station"}:</span>
                  <b className="text-slate-700">{NEAREST_FIRE[m.name].name}</b>
                  <span>(~{Math.round(NEAREST_FIRE[m.name].d)} km)</span>
                </p>
                <div className="flex flex-wrap gap-2">
                  <CallButton testId={`call-omme-${m.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} tel={m.ommeTel} label="OMME" sub={m.omme} color="amber" />
                  <CallButton testId={`call-911-${m.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} tel="911" label="911" color="red" />
                  <CallButton testId={`call-nmead-${m.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} tel="7877240124" label="NMEAD" color="navy" />
                  <CallButton
                    testId={`call-policia-${m.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    tel={MUNI_POLICE[m.name]?.tel || "7873432020"}
                    label={MUNI_POLICE[m.name] ? (lang === "es" ? "Policía Muni" : "Muni Police") : (lang === "es" ? "Policía PR" : "PR Police")}
                    sub={MUNI_POLICE[m.name]?.phone}
                    color={MUNI_POLICE[m.name] ? "green" : "navy"}
                  />
                  <CallButton testId={`call-bomberos-${m.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} tel="7873432330" label={lang === "es" ? "Bomberos" : "Fire Dept"} color="red" />
                </div>
                <div className="mt-2.5 flex items-center gap-2">
                  <button
                    data-testid={`share-${m.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => shareMuni(m)}
                    className="inline-flex items-center gap-1.5 rounded-md border-2 border-slate-300 px-3 py-1.5 text-xs font-bold text-slate-600 hover:border-amber-600 hover:text-amber-700 transition-colors duration-150 active:scale-[0.97]"
                  >
                    <Icon name="Share2" className="w-3.5 h-3.5" />
                    {copied === m.name ? (lang === "es" ? "¡Copiado!" : "Copied!") : (lang === "es" ? "Compartir" : "Share")}
                  </button>
                  <a
                    data-testid={`share-wa-${m.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    href={`https://wa.me/?text=${encodeURIComponent(shareText(m))}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-md border-2 border-green-700 px-3 py-1.5 text-xs font-bold text-green-700 hover:bg-green-700 hover:text-white transition-colors duration-150 active:scale-[0.97]"
                  >
                    <Icon name="MessageCircle" className="w-3.5 h-3.5" /> WhatsApp
                  </a>
                </div>
                {m.name === "San Juan" && (
                  <details data-testid="sj-precincts" className="group mt-3 rounded-md border border-slate-200 bg-[#F8FAFC] px-3.5 py-2.5">
                    <summary className="flex cursor-pointer list-none items-center gap-1.5 text-xs font-bold text-slate-600">
                      <Icon name="ChevronRight" className="w-3.5 h-3.5 transition-transform duration-200 group-open:rotate-90" />
                      {lang === "es" ? "Cuarteles por barrio — 11 precintos" : "Precincts by neighborhood — 11 precincts"}
                    </summary>
                    <ul className="mt-2 space-y-1 text-xs text-slate-600">
                      {SJ_PRECINCTS.map((p) => (
                        <li key={p}>• {p}</li>
                      ))}
                    </ul>
                    <p className="mt-2 text-[11px] leading-snug text-slate-500">
                      {lang === "es"
                        ? "Migración telefónica en curso: todos los precintos atienden por la línea central 787-480-2020."
                        : "Phone migration in progress: all precincts answered via central line 787-480-2020."}
                    </p>
                  </details>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {section === "agencias" && (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {agencies.length === 0 && <p className="text-slate-500">{tx(ui.directory.noResults, lang)}</p>}
          {agencies.map((a) => (
            <div key={a.id} data-testid={`agency-${a.id}`} className="flex flex-col rounded-lg border border-slate-300 bg-white p-6 transition-colors duration-200 hover:border-amber-500">
              <div className="flex items-center gap-3 mb-3">
                <span className={`flex items-center justify-center w-10 h-10 rounded-md text-white ${COLOR_STYLES[a.color].split(" ")[0]}`}>
                  <Icon name={a.icon} className="w-5 h-5" />
                </span>
                <h3 className="font-display text-lg font-bold text-slate-900 leading-tight">{tx(a.name, lang)}</h3>
              </div>
              <p className="flex-1 text-sm text-slate-600 leading-relaxed mb-4">{tx(a.desc, lang)}</p>
              <CallButton testId={`click-to-call-${a.id}`} tel={a.tel} label={tx(ui.directory.call, lang)} sub={a.phone} color={a.color} />
            </div>
          ))}
        </div>
      )}

      {section === "hospitales" && (
        <>
          <div data-testid="hospitals-note" className="mb-6 rounded-lg border-l-4 border-amber-600 border border-slate-300 bg-amber-50 p-4 sm:p-5">
            <p className="flex gap-2.5 text-sm text-amber-900 leading-relaxed">
              <Icon name="Info" className="w-5 h-5 shrink-0 text-amber-700" />
              {tx(ui.directory.hospitalsNote, lang)}
            </p>
          </div>
          {hospitals.length === 0 && <p className="text-slate-500">{tx(ui.directory.noResults, lang)}</p>}
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {hospitals.map((h, i) => (
              <div key={`${h.name}-${i}`} data-testid={`hospital-${h.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}`} className="rounded-lg border border-slate-300 bg-white p-5 transition-colors duration-200 hover:border-amber-500">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h3 className="font-display text-base font-bold text-slate-900 leading-snug">{h.name}</h3>
                  <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-bold text-slate-600 shrink-0">
                    {tx(REGIONS[h.region], lang)}
                  </span>
                </div>
                <p className="flex items-center gap-1.5 text-xs text-slate-500 mb-3.5">
                  <Icon name="MapPin" className="w-3.5 h-3.5" /> {h.town}
                </p>
                <div className="flex flex-wrap gap-2">
                  {h.tel ? (
                    <CallButton testId={`call-hospital-${i}`} tel={h.tel} label={h.phone} color="navy" />
                  ) : (
                    <CallButton testId={`call-hospital-911-${i}`} tel="911" label="911" color="red" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {section === "mapa" && <ShelterMap lowBandwidth={lowBandwidth} />}

      {section === "refugios" && <ShelterList region={region} query={query} />}
    </div>
  );
}
